/**
 * Generate themes end to end for any entry point: GenerateThemesModal plus
 * ThemeModal stacked on top (so suggestions stay open behind it) or one-click "Add".
 */
import { useState } from "react";

import { notifyError, notifySuccess } from "../../../../store/actions/notify";
import { createTheme, useQceDispatch, useThemes } from "../../../../store/state/qce/qceSlice";
import { qceErrMsg } from "../../../../store/state/qce/qceService";

import GenerateThemesModal from "./GenerateThemesModal";
import { CT_COLORS } from "./icons";
import ThemeModal from "./ThemeModal";
import type { ThemeDraft, ThemeWrite } from "./types";

interface GenerateThemesFlowProps {
  orgId: string;
  projectId: string;
  /** Generate from these recordings only (omit = the whole project). */
  storyIds?: string[];
  /** e.g. "In this recording" / "In 3 selected recordings". */
  scopeLabel?: string;
  onClose: () => void;
}

export function GenerateThemesFlow({ orgId, projectId, storyIds, scopeLabel, onClose }: GenerateThemesFlowProps) {
  const dispatch = useQceDispatch();
  const { themes, refetch } = useThemes(projectId);
  const [draft, setDraft] = useState<ThemeDraft | null>(null);

  const toast = (msg: string, type: "success" | "error" = "success") =>
    dispatch(type === "success" ? notifySuccess(msg) : notifyError(msg));

  const quickAdd = async (d: ThemeDraft) => {
    const body: ThemeWrite = {
      name: d.name,
      description: d.description,
      keywords: d.keywords,
      icon: d.icon || "house",
      color: d.color || CT_COLORS[themes.length % CT_COLORS.length].icon,
      exemplars: d.exemplars || [],
      skill: d.skill || "",
      from_skill: false,
      notes: "",
      links: [],
      projectId,
    };
    try {
      await dispatch(createTheme(body)).unwrap();
      toast(`Created "${d.name}"`);
    } catch (e: any) {
      toast(qceErrMsg(e, "Could not create theme"), "error");
    }
  };

  return (
    <>
      <GenerateThemesModal
        orgId={orgId}
        projectId={projectId}
        storyIds={storyIds}
        scopeLabel={scopeLabel}
        onPick={setDraft}
        onQuickAdd={quickAdd}
        onClose={onClose}
      />
      {draft && (
        <ThemeModal
          draft={draft}
          existingCount={themes.length}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            refetch();
            toast("Theme created");
          }}
          toast={toast}
        />
      )}
    </>
  );
}
